/**
 * Card component for displaying customer asset statistics.
 * Shows counts for owned assets and assets on location.
 */
import { CardHeader } from '@/components/card-header';
import { LinkRounded } from '@/constants/hardfin-standard-icons';
import { Customer } from '@/customer/api/types';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link } from '@tanstack/react-router';

interface Props {
  customer: Customer;
}

export function CustomerAssetStatsCard(props: Props) {
  return (
    <Paper variant="outlined">
      <CardHeader title="Assets" />
      <Divider />
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
        gap={2}
        margin={2}
      >
        <Stack
          padding={2}
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="h6">Owned</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h3">
              {formatNumber(props.customer.assetCount)}
            </Typography>
            <Tooltip arrow title="View owned assets">
              <Link to="/assets" search={{ customers: [props.customer.id] }}>
                <IconButton size="small">
                  <LinkRounded />
                </IconButton>
              </Link>
            </Tooltip>
          </Stack>
        </Stack>
        <Stack
          padding={2}
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="h6">On location</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h3">
              {formatNumber(props.customer.assetsAtLocation)}
            </Typography>
            <Tooltip arrow title="View assets on location">
              <Link
                to="/assets"
                search={{
                  customerLocations: [props.customer.id],
                }}
              >
                <IconButton size="small">
                  <LinkRounded />
                </IconButton>
              </Link>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
